import React, { createContext, useContext, useMemo } from 'react';

export type DateRangeValue = {
  from?: Date;
  to?: Date;
};

type DateRangeContextValue = DateRangeValue;

const DateRangeContext = createContext<DateRangeContextValue | null>(null);

function toDateOrUndef(v: unknown): Date | undefined {
  if (!v) return undefined;
  if (v instanceof Date) return isNaN(v.getTime()) ? undefined : v;

  // support ISO string
  if (typeof v === 'string') {
    const d = new Date(v);
    return isNaN(d.getTime()) ? undefined : d;
  }

  // support { from: "...", to: "..." } passing mistakes
  return undefined;
}

export const DateRangeProvider: React.FC<{
  value: DateRangeValue;
  children: React.ReactNode;
}> = ({ value, children }) => {
  const normalized = useMemo<DateRangeContextValue>(() => {
    const from = toDateOrUndef(value?.from);
    const to = toDateOrUndef(value?.to);

    // optional: ensure from <= to
    if (from && to && from.getTime() > to.getTime()) {
      return { from: to, to: from };
    }

    return { from, to };
  }, [value?.from, value?.to]);

  return <DateRangeContext.Provider value={normalized}>{children}</DateRangeContext.Provider>;
};

export function useDateRange(): DateRangeContextValue {
  const ctx = useContext(DateRangeContext);
  // if provider is missing, return empty range instead of crashing
  return ctx ?? { from: undefined, to: undefined };
}
